import { useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import { truncate } from 'src/components/pipelines/components/metadata/utils';
import { getCurrentUser } from 'src/selectors/user-selectors';
import authRequest from 'src/utils/fetch';

import {
  ENTRY_POINT_ID_CODE_REVIEWER,
  API_COLLECT_FEEDBACK,
  CAN_BE_CONTACTED_VALUE,
  CANNOT_BE_CONTACTED_VALUE,
  CONTACT_FIELD_ID,
  CONTEXT_FIELD_ID,
  FULL_NAME_FIELD_ID,
} from '../constants';
import type { FeedbackData, FeedbackResponse } from '../types';

type FeedbackContextEntry = {
  id: string;
  value: string | number | boolean;
};

const MAX_SUMMARY_LENGTH_CHARS = 100;
const singleLineTruncatedText = (
  text: string,
  length: number = MAX_SUMMARY_LENGTH_CHARS
) => {
  const singleLineText = text.replace(/\n/g, ' ');
  return truncate(singleLineText, length);
};

export const usePostAdditionalFeedback = (
  feedbackContextInput: FeedbackContextEntry[]
): readonly [
  { inProgress: boolean },
  {
    submit: (
      formData: FeedbackData,
      onSubmit?: () => void,
      onError?: () => void
    ) => Promise<Response | FeedbackResponse>;
  }
] => {
  const user = useSelector(getCurrentUser);
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (
      formData: FeedbackData,
      onSubmit?: () => void,
      onError?: () => void
    ) => {
      setSubmitting(true);

      const feedbackContext = [
        ...(feedbackContextInput ?? []),
        ...(formData.sentiment
          ? [
              {
                id: 'Thumb Rating',
                value: formData.sentiment,
              },
            ]
          : []),
        {
          id: 'Feedback Type',
          value: formData.feedbackType,
        },
        ...(formData.prUrl ? [{ id: 'PR Url', value: formData.prUrl }] : []),
        {
          id: 'hasCodeSuggestions',
          value: formData.hasCodeSuggestions,
        },
      ];

      const payload = user && {
        feedback: {
          entrypointId: ENTRY_POINT_ID_CODE_REVIEWER,
          entrypointUiVersion: '13.0.10',
          fields: [
            { id: 'product', value: 'bitbucket-cloud' },
            { id: 'hostingType', value: 'CLOUD' },
            {
              id: 'summary',
              value: singleLineTruncatedText(formData.feedbackDescription),
            },
            { id: 'description', value: formData.feedbackDescription },
            ...(formData.allowContact
              ? [{ id: 'email', value: user.email ?? '' }]
              : []),
            ...(formData.allowContact
              ? [{ id: 'aaidOrHash', value: user.account_id }]
              : []),
            {
              id: CONTACT_FIELD_ID,
              value: [
                formData.allowContact
                  ? CAN_BE_CONTACTED_VALUE
                  : CANNOT_BE_CONTACTED_VALUE,
              ],
            },
            { id: FULL_NAME_FIELD_ID, value: user.display_name ?? '' },
            {
              id: CONTEXT_FIELD_ID,
              value: feedbackContext
                .map(({ id, value }) => `${id}: ${String(value)}`)
                .join('\n'),
            },
          ],
        },
      };

      try {
        const response = await fetch(
          authRequest(API_COLLECT_FEEDBACK, {
            headers: {
              accept: 'application/json,text/javascript,*/*',
              'content-type': 'application/json',
            },
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            body: JSON.stringify({
              data: btoa(JSON.stringify(payload)),
            }),
          })
        );
        onSubmit?.();
        setSubmitting(false);
        return response;
      } catch {
        setSubmitting(false);
        const response: FeedbackResponse = {
          status: 'FAILED',
          message: 'Error',
        };
        onError?.();

        return response;
      }
    },
    [feedbackContextInput, user]
  );
  return [{ inProgress: submitting }, { submit }] as const;
};
